import { useState, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
// --- gsap ----------------------------------------------------------------
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// --- swiper ----------------------------------------------------------------
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
// --- lottie ----------------------------------------------------------------
import Lottie from "lottie-react";
import contactLogo from "../../assets/lotties/logo-purple.json";
// --- img ----------------------------------------------------------------
import { kakaoIcon, linkIcon, servicePortfolio01, servicePortfolio02, servicePortfolio03 } from "../../assets/images";
// --- style ----------------------------------------------------------------
import "./Service.scss";

gsap.registerPlugin(ScrollTrigger);

function CreationSuite() {
  // --- 데이터 ----------------------------------------------------------------
  // 워크 플로우
  const workFlow = [
    {
      key: "concept",
      labelKo: "기획 / 상담",
      labelEn: "CONCEPT",
      day: "DAY 1",
      title: "아이디어가 음악이 되는 첫 걸음",
      desc: ["콘셉트 회의", "장르 및 노래 선정"],
    },
    {
      key: "creation",
      labelKo: "편곡",
      labelEn: "CREATION",
      day: "DAY 2~6",
      title: "상세한 곡 선정 및 편곡",
      desc: ["감정 중심의 작곡, 메시지 기반 가사 생성", "원하는 분위기의 악기/키", "시안 확인 가능 (수정 1회)"],
    },
    {
      key: "performance",
      labelKo: "보컬 녹음 & 디렉팅",
      labelEn: "PERFORMANCE",
      day: "DAY 7",
      title: "당신만의 완벽한 음악을 위한 보컬 디테일",
      desc: ["스튜디오 레코딩 · 튠/에딧 · 감정 연출", "디렉터와 녹음하며 완벽한 사운드 컨디션", "간단한 보컬 트레이닝"],
    },
    {
      key: "visual",
      labelKo: "프로필 촬영 / 영상 제작",
      labelEn: "VISUAL BRANDING",
      day: "DAY 7",
      title: "아티스트만의 무드를 담아",
      desc: [
        "프로필 · 영상 · 메이킹까지 아티스트의 비주얼 아이덴티티 구축",
        "완성된 영상은 MCN 플랫폼 및 SNS 계정에 자동 연동",
      ],
    },
    {
      key: "engineering",
      labelKo: "튠 & 믹싱 & 마스터링",
      labelEn: "ENGINEERING",
      day: "DAY 8~12",
      title: "공들인 사운드, 완벽한 음악의 완성",
      desc: [
        "음질 밸런싱 · 사운드 디자인",
        "톤 밸런스 / 공간감 / 스테레오 이미지 조정",
        "글로벌 DSP 기준에 맞춘 음원 포맷으로 마스터링 완료",
      ],
    },
    {
      key: "distribution",
      labelKo: "음원 유통 & 발매",
      labelEn: "DISTRIBUTION",
      day: "DAY 14",
      title: "“세상에 이름을 올리는 마지막 단계”",
      desc: ["멜론, 스포티파이, 유튜브뮤직 등 DSP 등록", "아트 커버 제작 / 아티스트 커버 제작"],
      title2: "이 모든 과정까지 함께",
      desc2: [
        "등록된 곡은 2주안에 유통 완료",
        "일정 조율 가능",
        "일정 예약 완료 시 녹음 촬영 당일 스케줄 변경 불가",
        "변경 시 추가요금 발생",
      ],
    },
    {
      key: "agency",
      labelKo: "MOB 에이전시 등록",
      labelEn: "선택",
      day: "“여기서 끝이 아닙니다. 무대는 더 커집니다.”",
      title: "MOB MCN Agency 등록 안내",
      desc: ["완성된 아티스트는 MOB MCN에 등록할 수 있으며\n 광고 / 행사 / 공연 / 라이브 콜라보 기회를 제공합니다."],
      title2: "브랜드 광고 & 캠페인 매칭",
      desc2: [
        "AI 분석 기반으로 브랜드와 크리에이터 매칭",
        "라이브 공연 & 버스킹 무대 연결",
        "오프라인 공연 및 페스티벌 참여 연계",
        "음원 수익 + 영상 수익 통합 관리",
        "유통 + 플랫폼 수익 + 제작 광고까지 일괄 정산",
      ],
    },
  ];
  const [activeFlowKey, setActiveFlowKey] = useState(workFlow[0].key);
  const activeFlow = workFlow.find((v) => v.key === activeFlowKey) ?? workFlow[0];
  const isAgency = activeFlow.key === "agency";

  // --- 애니메이션 ----------------------------------------------------------------
  const scopeRef = useRef(null);
  // motion 1 animation
  useLayoutEffect(() => {
    const el = scopeRef.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const cards = Array.from(el.children);

      gsap.set(cards, { autoAlpha: 0, y: 24 });

      gsap.to(cards, {
        autoAlpha: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.45,
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true,
        },
      });
    }, scopeRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <div className="content-section creation">
        {/* === 패키지 === */}
        <ul className="creation__package-list max-12" ref={scopeRef}>
          <li>
            <strong>음원제작 패키지</strong>
            <p className="desc">
              나와 음악이 세상에 나오는 첫 걸음
              <br />
              작곡부터 발매까지,
              <br />한 번의 선택으로 완성되는 원스톱 패키지
            </p>
            <ul className="tag-list">
              <li>작곡</li>
              <li>보컬 녹음</li>
              <li>튠/에딧</li>
              <li>믹싱</li>
              <li>마스터링</li>
              <li>아트커버 제작</li>
              <li>음반 유통 대행</li>
            </ul>
            <div className="price">
              <span className="badge sale">오픈 할인</span>
              <p>
                <del>1,000,000원</del>
                800,000원
              </p>
            </div>
          </li>

          <li>
            <strong>보컬 비주얼 아티스트 패키지</strong>
            <p className="desc">
              프로필·영상·라이브까지 모두 포함된
              <br />
              완성형 아티스트 런칭 패키지
              <b>보컬 아티스트 패키지 전체 포함</b>
            </p>
            <ul className="tag-list">
              <li>프로필 촬영</li>
              <li>메이킹 필름 제작</li>
              <li>라이브 클립 촬영</li>
            </ul>
            <div className="price">
              <span className="badge sale">오픈 할인</span>
              <p>
                <del>1,500,000원</del>
                1,000,000원
              </p>
            </div>
          </li>

          <li>
            <strong>프로듀싱 체험 패키지</strong>
            <p className="desc">
              직접 프로듀서로 참여하기
              <br />
              작곡·보컬 섭외·녹음 과정을 함께할 수 있는
              <br />
              MOB만의 공동 제작형 패키지
            </p>
            <ul className="tag-list">
              <li>작곡 기획 참여</li>
              <li>디렉팅 동행</li>
              <li>믹싱/마스터링</li>
              <li>보컬 섭외 및 녹음 포함</li>
              <li>유통 대행 선택</li>
            </ul>
            <div className="price">
              <span className="badge basic">기본</span>
              <p>
                <small>(곡 규모에 따라 협의)</small>
                1,000,000원~
              </p>
            </div>
          </li>
        </ul>

        {/* === MOB WORKFLOW === */}
        <section className="creation__workflow">
          <div className="content-section__title">
            <span>AI로 만든 음악, 이렇게 발매됩니다.</span>
            <h3 className="gfont">MOB WORKFLOW</h3>
          </div>
          <div className="workflow-inner">
            <div className="max-12">
              {/* --- 리스트 */}
              <div className="workflow-swiper">
                <Swiper
                  className="workflow-list"
                  modules={[FreeMode]}
                  slidesPerView="auto"
                  spaceBetween={8}
                  freeMode
                  preventClicks={true}
                  touchStartPreventDefault={false}
                  noSwiping={false}
                  breakpoints={{
                    1023: {
                      direction: "vertical",
                      allowTouchMove: false,
                      spaceBetween: 8,
                    },
                    0: {
                      direction: "horizontal",
                      allowTouchMove: true,
                      spaceBetween: 4,
                    },
                  }}
                >
                  {workFlow.map((item) => (
                    <SwiperSlide key={item.key} className="workflow-list__slide" style={{ width: "auto" }}>
                      <button
                        type="button"
                        className={activeFlow.key === item.key ? "active" : ""}
                        onClick={() => setActiveFlowKey(item.key)}
                        aria-selected={activeFlow.key === item.key}
                      >
                        <strong>{item.labelKo}</strong>
                        <span>{item.labelEn}</span>
                      </button>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              {/* --- 컨텐츠 --- */}
              <div className="workflow-content">
                <h4 className="title">
                  {isAgency ? "결과&확장" : activeFlow.labelKo}
                  <span>{isAgency ? "BEYOND MUSIC" : activeFlow.labelEn}</span>
                </h4>
                <p className="date gfont">{activeFlow.day}</p>
                <div className="description">
                  <div>
                    <strong>{activeFlow.title}</strong>
                    <ul>
                      {activeFlow.desc.map((text, idx) => (
                        <li key={idx}>{text}</li>
                      ))}
                    </ul>
                  </div>
                  {activeFlow.title2 && (
                    <div>
                      <strong>{activeFlow.title2}</strong>
                      <ul>
                        {activeFlow.desc2.map((text, idx) => (
                          <li key={idx}>{text}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* === WHAT WE CREATE === */}
        <section className="creation__create max-12">
          <div className="content-section__title">
            <span>WHAT WE CREATE</span>
            <h3 className="gfont">WHAT WE CREATE</h3>
          </div>
          <ul>
            <li>
              <div className="desc">
                <h4>
                  뮤직비디오 촬영 제작
                  <span>(Music Shoot)</span>
                </h4>
                <p>
                  당신의 음악을 시작으로 기록합니다. <br />
                  콘셉트 기획부터 촬영, 편집까지 완성형 MV 제작 <br />
                  촬영 현장 영상 또는 콘티 이미지 <br />
                  AI 뮤직비디오를 애니메이션 형으로 제작 가능합니다. <br />
                </p>
              </div>
            </li>
            <li>
              <div className="desc">
                <h4>
                  Melody Latte
                  <span>(Recording Day Gift)</span>
                </h4>
                <p>
                  녹음 당일, 영감이 담긴 한 잔. <br />
                  MOB Signature Melody Latte <br />
                  <b>무료 1잔 증정</b>
                </p>
              </div>
            </li>
            <li>
              <div className="desc">
                <h4>
                  보컬 아티스트 피처링
                  <span>(Vocal Featuring)</span>
                </h4>
                <p>
                  장르에 맞는 보컬리스트 섭외로 완성도 상승하고 <br />
                  보컬 선택 가능합니다.
                </p>
              </div>
            </li>
            <li>
              <div className="desc">
                <h4>
                  실제 악기 연주 녹음
                  <span>(Live Instrument Session)</span>
                </h4>
                <p>
                  전문 세션 연주자가 참여하여 <br />
                  음악의 질감을 업그레이드 합니다.
                </p>
              </div>
            </li>
            <li>
              <div className="desc">
                <h4>
                  MOB 공동 마케팅
                  <span>(MOB Co-Marketing)</span>
                </h4>
                <p>
                  발매 후 MOB 채널과 제휴 플랫폼에서 <br />
                  프로모션 지원
                </p>
              </div>
            </li>
            <li>
              <div className="desc">
                <h4>
                  커스텀 LP 소장용
                  <span>(Custom Vinyl LP)</span>
                </h4>
                <p>
                  당신의 곡을 실제 LP로 제작해 드립니다. <br />
                  유일한 아티스트 컬렉션 <br />
                  회전하는 LP GIF / 앨범 커버 삽입
                </p>
              </div>
            </li>
          </ul>
        </section>

      {/* === PORTFOLIO === */}
      <section className="creation__portfolio max-12">
        <div className="content-section__title">
          <span>MOB STUDIO가 만들어온 작품들, <span className="spacing">기술과 감성이 만난 음악의 결과를 직접 확인하세요</span></span>
          <h3 className="gfont">PORTFOLIO</h3>
        </div>
        <Swiper
          className="portfolio-swiper"
          slidesPerView="auto"
          spaceBetween={10}
          watchOverflow={true}
          preventClicks={true}
          touchStartPreventDefault={true}
          freeMode={false}
          resistance={false}
          resistanceRatio={0}
          breakpoints={{
            0: { allowTouchMove: true },
            767: { allowTouchMove: false },
          }}          
        >
          <SwiperSlide style={{ width: "auto" }}>
            <Link to="/portfolio" className="scale-wrap">
              <span
                className="scale-bg"
                style={{ backgroundImage: `url(${servicePortfolio01})` }}
                aria-hidden="true"
              />
              <h4>
                <span>Artist</span>
                <img src={linkIcon} alt="" aria-hidden="true" />
              </h4>
            </Link>
          </SwiperSlide>

            <SwiperSlide style={{ width: "auto" }}>
              <Link to="/recruit" className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${servicePortfolio02})` }}
                  aria-hidden="true"
                />
                <h4>
                  <span>Together With</span>
                  <img src={linkIcon} alt="" aria-hidden="true" />
                </h4>
              </Link>
            </SwiperSlide>

            <SwiperSlide style={{ width: "auto" }}>
              <Link to="/music" className="scale-wrap">
                <span
                  className="scale-bg"
                  style={{ backgroundImage: `url(${servicePortfolio03})` }}
                  aria-hidden="true"
                />
                <h4>
                  <span>Music</span>
                  <img src={linkIcon} alt="" aria-hidden="true" />
                </h4>
              </Link>
            </SwiperSlide>
          </Swiper>
        </section>

        {/* === INQUIRY === */}
        <div className="content-section__contact">
          <p className="desc">ALL MAKERS, ONE STAGE. 지금 당신의 무대를 시작하세요</p>
          <h3 className="gfont title">RESERVATION INQUIRY</h3>
          <Lottie animationData={contactLogo} loop autoplay className="contact-lottie" />

          <div className="kakao-link">
            <span>지금 시작해 보세요.</span>
            <a href="https://pf.kakao.com/_QLTxgn" target="_blank" rel="noopener noreferrer">
              <img src={kakaoIcon} alt="" aria-hidden="true" />
              카카오 채널 문의
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export default CreationSuite;
